<template>
  <div
    class="pull-to-refresh"
    @touchstart="holdTapStartPos"
    @touchmove="setCalculatedPullDistance"
    @touchend="judgePullUpOrPullDown"
  >
    <!-- リフレッシュ操作時レイアウト -->
    <div
      class="refresh-indicator flex justify-center items-center"
      :class="isSwiping ? '' : 'transitionHeight'"
      :style="{height: `${pullDownHeight}px`}"
    >
      <div
        v-show="isRefreshing"
        class="bg-white h-12 w-12 rounded-[39px] flex justify-center items-center"
      >
        <BaseLoading width="16" />
      </div>
    </div>
    <!-- コンテンツ -->
    <div class="content">
      <slot></slot>
    </div>
    <div
      class="refresh-indicator flex justify-center items-center"
      :class="isSwiping ? '' : 'transitionHeight'"
      :style="{height: `${pullUpHeight}px`}"
    >
      <div
        v-show="isPaging"
        class="bg-white h-12 w-12 rounded-[39px] flex justify-center items-center"
      >
        <BaseLoading width="16" />
      </div>
    </div>
  </div>
</template>
<script>
import BaseLoading from '@/components/atoms/BaseLoading.vue'
/** リフレッシュ時の判定値 */
const REFRESH_HIGHT = 72
/** ページネーション時の判定値 */
const PAGINATION_HIGHT = 72

/** スクロール位置の状態 */
const SCROLL_STATUS = {NONE: 'NONE', TOP: 'TOP', BOTTOM: 'BOTTOM'}

/** スワイプ操作 */
const SWIPE = {UP: 'UP', DOWN: 'DOWN'}

/**
 * [Readme]
 * 本コンポーネントを正常に利用するために、親コンポーネント側で必要な作業がある。
 * ・リフレッシュ完了時、ページネーション完了時のイベントハンドリングを実装
 *   → 完了処理を実行
 * ・スクロール対象の要素idに対しscrollイベントを付与。
 *   イベント時の処理は本コンポーネントに定義しているhandleScrollを定義する。
 */
const PullToRefreshAndPagination = {
  name: 'PullToRefreshAndPagination',
  components: {BaseLoading},
  emits: ['refresh', 'pagination', 'scroll'],
  data() {
    return {
      startY: 0, // タッチ開始位置
      pullDownHeight: 0, // 要素を引き下げた高さ
      pullUpHeight: 0, // 要素を引き上げた高さ
      isRefreshing: false, // リフレッシュ中であるか
      isPaging: false, // ページネーション中であるか
      isSwiping: false, // スワイプ操作中であるか
      scrollStatus: SCROLL_STATUS.TOP, // スクロール位置の状態
    }
  },
  methods: {
    /**
     * タッチ開始時処理
     * タッチ開始位置を保持する
     * @param {TouchEvent} event タッチイベント
     */
    holdTapStartPos(event) {
      // スクロール位置が最上部もしくは最下部以外であれば、何もしない
      if (this.scrollStatus === SCROLL_STATUS.NONE) return

      // タッチ開始位置を保持
      this.startY = event.touches[0].pageY // eslint-disable-line no-magic-numbers
    },
    /**
     * タッチ移動時処理
     * スワイプの移動距離を算出し、引き上げ/引き下げ距離として保持する
     * @param {TouchMove} event タッチイベント
     */
    setCalculatedPullDistance(event) {
      // スクロール位置が最上部もしくは最下部以外であれば、何もしない
      if (this.scrollStatus === SCROLL_STATUS.NONE) return
      this.isSwiping = true
      const currentY = event.touches[0].pageY // eslint-disable-line no-magic-numbers
      const diffTouchY = currentY - this.startY

      // 移動していない場合は何もしない
      // eslint-disable-next-line no-magic-numbers
      if (diffTouchY === 0) return

      // eslint-disable-next-line no-magic-numbers
      const swipe = 0 < diffTouchY ? SWIPE.DOWN : SWIPE.UP // スワイプ向き
      const noAdjustH = 0
      const minH = 0 // 高さを算出する際の最低値(負の値にならないように)
      const status = this.scrollStatus

      if (status === SCROLL_STATUS.TOP && swipe === SWIPE.DOWN) {
        // スクロール位置が最上部の場合であり、下向きにスワイプした場合、引き下げ高の値を算出し更新
        const adjustmentH = this.isRefreshing ? REFRESH_HIGHT : noAdjustH
        this.pullDownHeight = Math.max(minH, diffTouchY + adjustmentH)
      } else if (status === SCROLL_STATUS.BOTTOM && swipe === SWIPE.UP) {
        // スクロール位置が最下部の場合であり、上向きにスワイプした場合、引き上げ高の値を算出し更新
        const adjustmentH = this.isPaging ? PAGINATION_HIGHT : noAdjustH
        this.pullUpHeight = Math.max(minH, Math.abs(diffTouchY) + adjustmentH)
      }
    },
    /**
     * タッチ終了時処理
     * 上下どちらにスワイプしたかを判断し、それぞれの処理を呼びだす
     * @param {TouchEvent} event タッチイベント
     */
    judgePullUpOrPullDown() {
      this.isSwiping = false
      const zero = 0
      if (zero < this.pullDownHeight) {
        this.pullDownAction()
      } else if (zero < this.pullUpHeight) {
        this.pullUpAction()
      }
    },
    /**
     * 引き上げ操作時処理
     * ページを一定以上引き上げた場合、ページネーション処理を行う
     */
    pullUpAction() {
      // リフレッシュ中の場合、リフレッシュ時の高さに戻し、以降は何も行わない
      if (this.isPaging) {
        this.pullUpHeight = PAGINATION_HIGHT
        return
      }

      // 一番下のスクロール位置で、一定以上引き下げた場合、ページネーション操作と判断する
      const isNeedPagination =
        this.scrollStatus === SCROLL_STATUS.BOTTOM &&
        PAGINATION_HIGHT < this.pullUpHeight
      if (isNeedPagination) {
        this.isPaging = true
        // リフレッシュ中は一定の高さを引き下げた状態にする為、高さを変更
        this.pullUpHeight = PAGINATION_HIGHT
        // 親要素にリフレッシュ通知
        this.$emit('pagination', this.paginationDone)
      } else {
        // リフレッシュ操作ではないと判断した場合、要素の引き下げ高を元に戻す
        this.pullUpHeight = 0
        this.pullDownHeight = 0
      }
    },
    /**
     * 引き下げ操作時処理
     * ページを一定以上引き下げた場合、リフレッシュ処理を行う
     */
    pullDownAction() {
      // リフレッシュ中の場合、リフレッシュ時の高さに戻し、以降は何も行わない
      if (this.isRefreshing) {
        this.pullDownHeight = REFRESH_HIGHT
        return
      }

      // 一番上のスクロール位置の状態で一定以上引き下げた場合、リフレッシュ操作と判断する
      const isNeedRefresh =
        this.scrollStatus === SCROLL_STATUS.TOP &&
        REFRESH_HIGHT < this.pullDownHeight
      if (isNeedRefresh) {
        this.isRefreshing = true
        // リフレッシュ中は一定の高さを引き下げた状態にする為、高さを変更
        this.pullDownHeight = REFRESH_HIGHT
        this.$emit('refresh', this.refreshDone)
      } else {
        // リフレッシュ操作ではないと判断した場合、要素の引き下げ高を元に戻す
        this.pullUpHeight = 0
        this.pullDownHeight = 0
      }
    },
    /**
     * リフレッシュ完了時の終了処理
     */
    refreshDone() {
      this.isRefreshing = false
      this.pullUpHeight = 0
      this.pullDownHeight = 0
    },
    /**
     * ページネーション完了時の終了処理
     * @param {String} targetId スクロール対象の要素ID
     */
    paginationDone(targetId) {
      // スクロール位置が最下部の場合、要素が追加された時に不自然な挙動となる。わずかにでも最下部でなければ回避できるため、スクロール位置をずらす
      const el = document.getElementById(targetId)
      if (el != null) el.scrollTop = el.scrollTop - 0.01 // eslint-disable-line no-magic-numbers

      this.isPaging = false
      this.pullUpHeight = 0
      this.pullDownHeight = 0
    },
    /**
     * スクロール操作時処理
     */
    handleScroll(event) {
      const el = document.getElementById(event.target.id)
      // 画面遷移等で要素が破棄されている場合、何も行わない
      if (el == null) return
      const {scrollTop, clientHeight, scrollHeight} = el
      this.$emit('scroll', scrollTop)

      // スクロールが一番上まで行われているか
      const isScrollTop = scrollTop === 0 // eslint-disable-line no-magic-numbers
      // スクロールが一番下まで行われているか (scrollTopが浮動小数点を含む事、scrollHightが動的に変わる事を考慮し、ある程度の幅を設けて判断)
      const scrollBottomHight = scrollHeight - clientHeight - scrollTop
      const isScrollBottom = Math.abs(scrollBottomHight) < 60 // eslint-disable-line no-magic-numbers

      // スクロール状態を保持
      if (isScrollTop) {
        this.scrollStatus = SCROLL_STATUS.TOP
      } else if (isScrollBottom) {
        this.scrollStatus = SCROLL_STATUS.BOTTOM
      } else {
        this.scrollStatus = SCROLL_STATUS.NONE
      }
    },
  },
}
export default PullToRefreshAndPagination
</script>
<style scoped>
.pull-to-refresh {
  overflow: hidden;
}
.transitionHeight {
  transition: height 0.3s;
}
.refresh-indicator {
  text-align: center;
  background-color: #f2f3f5;
}
.content {
  /* カスタマイズ可能なスタイル */
}
</style>
